/**
 * Date range presets — Voluum-style full list.
 *
 * Shared, reusable helpers. Used by SMSWiser stats, list/tag-rules, flow
 * optimizer date pickers, and the per-flow metrics range picker.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  addDaysToIso,
  addMonthsToIso,
  firstDayOfMonthIso,
  firstDayOfYearIso,
  lastDayOfMonthIso,
  lastDayOfYearIso,
  todayIsoDateParis,
} from '@/features/smswiser-campaigns/utils/dateFormat';

export const STATS_DATE_RANGE_PRESETS = [
  { code: 'today', label: 'Today' },
  { code: 'yesterday', label: 'Yesterday' },
  { code: 'last-24-hours', label: 'Last 24 hours' },
  { code: 'last-48-hours', label: 'Last 48 hours' },
  { code: 'last-3-days', label: 'Last 3 days' },
  { code: 'last-7-days', label: 'Last 7 days' },
  { code: 'last-14-days', label: 'Last 14 days' },
  { code: 'this-month', label: 'This month' },
  { code: 'last-30-days', label: 'Last 30 days' },
  { code: 'last-month', label: 'Last month' },
  { code: 'this-year', label: 'This year' },
  { code: 'last-year', label: 'Last year' },
  { code: 'custom', label: 'Custom' },
] as const;

export type StatsDateRangePreset = (typeof STATS_DATE_RANGE_PRESETS)[number]['code'];

/** Presets grouped for UI display */
export const PRESET_GROUPS: { title: string; codes: StatsDateRangePreset[] }[] = [
  { title: 'Quick', codes: ['today', 'yesterday'] },
  { title: 'Recent', codes: ['last-24-hours', 'last-48-hours', 'last-3-days', 'last-7-days', 'last-14-days', 'last-30-days'] },
  { title: 'Periods', codes: ['this-month', 'last-month', 'this-year', 'last-year'] },
];

export function getStatsDateRangeLabel(code: string): string {
  const found = STATS_DATE_RANGE_PRESETS.find((p) => p.code === code);
  return found?.label ?? code;
}

export function getRangeFromPreset(preset: StatsDateRangePreset): { from: string; to: string } | null {
  if (preset === 'custom') return null;
  const today = todayIsoDateParis();
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const yesterday = addDaysToIso(today, -1);
      return { from: yesterday, to: yesterday };
    }
    case 'last-24-hours':
      return { from: today, to: today };
    case 'last-48-hours': {
      const yesterday = addDaysToIso(today, -1);
      return { from: yesterday, to: today };
    }
    case 'last-3-days':
      return { from: addDaysToIso(today, -2), to: today };
    case 'last-7-days':
      return { from: addDaysToIso(today, -6), to: today };
    case 'last-14-days':
      return { from: addDaysToIso(today, -13), to: today };
    case 'this-month':
      return { from: firstDayOfMonthIso(today), to: today };
    case 'last-30-days':
      return { from: addDaysToIso(today, -29), to: today };
    case 'last-month': {
      const lastMonth = addMonthsToIso(today, -1);
      return { from: firstDayOfMonthIso(lastMonth), to: lastDayOfMonthIso(lastMonth) };
    }
    case 'this-year':
      return { from: firstDayOfYearIso(today), to: today };
    case 'last-year': {
      const lastYear = addDaysToIso(firstDayOfYearIso(today), -1);
      return { from: firstDayOfYearIso(lastYear), to: lastDayOfYearIso(lastYear) };
    }
    default:
      return null;
  }
}

export function getPresetFromRange(
  fromDate: string,
  toDate: string,
): StatsDateRangePreset {
  const today = todayIsoDateParis();
  const yesterday = addDaysToIso(today, -1);

  if (fromDate === today && toDate === today) return 'today';
  if (fromDate === yesterday && toDate === yesterday) return 'yesterday';
  const last48 = getRangeFromPreset('last-48-hours');
  if (last48 && fromDate === last48.from && toDate === last48.to) return 'last-48-hours';
  const last3 = getRangeFromPreset('last-3-days');
  if (last3 && fromDate === last3.from && toDate === last3.to) return 'last-3-days';
  const last7 = getRangeFromPreset('last-7-days');
  if (last7 && fromDate === last7.from && toDate === last7.to) return 'last-7-days';
  const last14 = getRangeFromPreset('last-14-days');
  if (last14 && fromDate === last14.from && toDate === last14.to) return 'last-14-days';
  const thisMonth = getRangeFromPreset('this-month');
  if (thisMonth && fromDate === thisMonth.from && toDate === thisMonth.to) return 'this-month';
  const last30 = getRangeFromPreset('last-30-days');
  if (last30 && fromDate === last30.from && toDate === last30.to) return 'last-30-days';
  const lastMonth = getRangeFromPreset('last-month');
  if (lastMonth && fromDate === lastMonth.from && toDate === lastMonth.to) return 'last-month';
  const thisYear = getRangeFromPreset('this-year');
  if (thisYear && fromDate === thisYear.from && toDate === thisYear.to) return 'this-year';
  const lastYear = getRangeFromPreset('last-year');
  if (lastYear && fromDate === lastYear.from && toDate === lastYear.to) return 'last-year';

  return 'custom';
}
